import { lazy, Suspense, type ComponentType } from 'react';
import { Route, Routes } from 'react-router';
import { brandFromHost } from '@brands/shared';
import { HomeRedirect, RequireAdmin, RequireAuth } from './auth/guards';
import { AppLayout } from './components/AppLayout';
import { Loading } from './components/Loading';
import { AccountPage } from './pages/AccountPage';
import { BrandRoutePage } from './pages/BrandRoutePage';
import { CampaignPage } from './pages/CampaignPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

// El panel de admin y la página de diseño se cargan aparte: el cliente nunca los descarga.
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const AccessPage = page(() => import('./pages/AccessPage'), 'AccessPage');
const AdminPage = page(() => import('./pages/AdminPage'), 'AdminPage');
const BrandAdminPage = page(() => import('./pages/BrandAdminPage'), 'BrandAdminPage');
const NewBrandPage = page(() => import('./pages/NewBrandPage'), 'NewBrandPage');
const CampaignAdminPage = page(() => import('./pages/CampaignAdminPage'), 'CampaignAdminPage');
const NewCampaignPage = page(() => import('./pages/NewCampaignPage'), 'NewCampaignPage');
const TokensPage = page(() => import('./pages/TokensPage'), 'TokensPage');

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route
          path="/"
          element={
            brandFromHost(window.location.host) ? (
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            ) : (
              <HomeRedirect />
            )
          }
        >
          <Route index element={<BrandRoutePage />} />
        </Route>
        <Route path="/entrar" element={<LoginPage />} />
        <Route path="/diseno" element={<TokensPage />} />
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route path="/campanas" element={<CampaignsPage />} />
          <Route path="/c/:id" element={<CampaignPage />} />
          <Route path="/cuenta" element={<AccountPage />} />
          <Route
            path="/admin/marcas/nueva"
            element={
              <RequireAdmin>
                <NewBrandPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/m/:id"
            element={
              <RequireAdmin>
                <BrandAdminPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/accesos"
            element={
              <RequireAdmin>
                <AccessPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/nueva"
            element={
              <RequireAdmin>
                <NewCampaignPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/c/:id"
            element={
              <RequireAdmin>
                <CampaignAdminPage />
              </RequireAdmin>
            }
          />
          {/* Links públicos: /{cliente}/{campaña} y, en {cliente}.brands.mx, /{campaña} */}
          <Route path="/:clientSlug/:slug" element={<BrandRoutePage />} />
          <Route path="/:slug" element={<BrandRoutePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
